/**
 * `ApiClient` — typed wrapper over the Portal_API HTTP surface.
 *
 * Every method returns a promise typed against the shared shapes in
 * `@aim/types`. Non-2xx responses are converted into rejected promises
 * carrying an `ApiError` whose `envelope` is the parsed `ApiErrorEnvelope`
 * body. When the response body is not valid JSON — for example, an HTML
 * 502 emitted by an upstream proxy — a synthetic `NETWORK_ERROR` envelope
 * is used instead so callers always see the same shape.
 *
 * Consumed by:
 *   - `packages/web/src/main.ts`      (task 7.7, boot & wiring)
 *   - `packages/web/src/views/*.ts`   (upload, modal, registry, publish)
 *
 * Requirements covered:
 *   - 17.3 (authoring API surface)
 *   - 17.4 (surface error to UI, do not silently succeed)
 *   - 20.5 (published snapshot round-trip is exercised via the return
 *          shape of `publish`; the actual gzip round-trip is validated
 *          by `SnapshotClient` and by the publish task tests)
 */
import type {
  ApiErrorEnvelope,
  Case,
  CaseDraft,
  DeckExtract,
  PublishResult,
} from "@aim/types";

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

export interface ApiClient {
  listCases(): Promise<Case[]>;
  parseUpload(file: File): Promise<DeckExtract>;
  createCase(draft: CaseDraft): Promise<Case>;
  patchCase(id: string, patch: Partial<CaseDraft>): Promise<Case>;
  deleteCase(id: string): Promise<void>;
  fetchDeckBlob(id: string): Promise<Blob>;
  resetBaseline(): Promise<{ removedFiles: number }>;
  clearAll(): Promise<{ removedCases: number }>;
  publish(opts?: { includeDecks?: boolean }): Promise<PublishResult>;
}

export interface ApiClientOptions {
  /** Absolute or origin-relative base for API routes, e.g. `"/api"`. */
  apiBase: string;
  /** Injectable `fetch` for tests; defaults to `globalThis.fetch`. */
  fetchImpl?: typeof fetch;
}

/**
 * Rejected-promise carrier for every non-2xx response from Portal_API.
 *
 * The `envelope` field is the parsed `ApiErrorEnvelope` (or a synthesized
 * one if the body was not valid JSON). `code` and `status` mirror the
 * envelope and HTTP status for callers that only need the summary.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly envelope: ApiErrorEnvelope;

  constructor(status: number, envelope: ApiErrorEnvelope) {
    super(envelope.message);
    this.name = "ApiError";
    this.status = status;
    this.code = envelope.code;
    this.envelope = envelope;
    // Restore prototype chain across the `extends Error` boundary so
    // `instanceof ApiError` keeps working after TS downlevel emit.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

/** Build a synthetic envelope for non-JSON (e.g. HTML 502) error bodies. */
function synthesizeNetworkEnvelope(status: number): ApiErrorEnvelope {
  return { code: "NETWORK_ERROR", message: `HTTP ${String(status)}` };
}

/**
 * Type guard for a parsed error body. Accepts anything with the two
 * required string fields; ignores extras (`details`, etc.).
 */
function isEnvelope(value: unknown): value is ApiErrorEnvelope {
  if (value === null || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return typeof v.code === "string" && typeof v.message === "string";
}

/**
 * Read the body of a non-2xx response as an `ApiErrorEnvelope`.
 * Non-JSON bodies collapse to `NETWORK_ERROR`.
 */
async function readErrorEnvelope(
  response: Response,
): Promise<ApiErrorEnvelope> {
  let bodyText: string;
  try {
    bodyText = await response.text();
  } catch {
    return synthesizeNetworkEnvelope(response.status);
  }

  if (bodyText.length === 0) {
    return synthesizeNetworkEnvelope(response.status);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(bodyText);
  } catch {
    return synthesizeNetworkEnvelope(response.status);
  }

  if (!isEnvelope(parsed)) {
    return synthesizeNetworkEnvelope(response.status);
  }
  return parsed;
}

/** Normalize `apiBase` by stripping a trailing slash if present. */
function normalizeBase(apiBase: string): string {
  return apiBase.endsWith("/") ? apiBase.slice(0, -1) : apiBase;
}

/**
 * Construct an `ApiClient` bound to a given `apiBase`.
 *
 * The client is stateless beyond the captured options; it can be reused
 * across the SPA lifetime. Every method delegates to `fetchImpl` and,
 * on any non-2xx response, rejects with an `ApiError`.
 */
export function createApiClient(opts: ApiClientOptions): ApiClient {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch;
  if (typeof fetchImpl !== "function") {
    throw new TypeError(
      "createApiClient: no fetch implementation available in this environment.",
    );
  }
  const base = normalizeBase(opts.apiBase);

  async function request(
    path: string,
    init?: RequestInit,
  ): Promise<Response> {
    const response = await fetchImpl(`${base}${path}`, init);
    if (!response.ok) {
      const envelope = await readErrorEnvelope(response);
      throw new ApiError(response.status, envelope);
    }
    return response;
  }

  async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await request(path, init);
    return (await response.json()) as T;
  }

  return {
    listCases(): Promise<Case[]> {
      return requestJson<Case[]>("/cases");
    },

    parseUpload(file: File): Promise<DeckExtract> {
      const form = new FormData();
      form.append("file", file);
      // Do NOT set Content-Type here; the browser (and Node's fetch)
      // must set the multipart boundary automatically.
      return requestJson<DeckExtract>("/parse", {
        method: "POST",
        body: form,
      });
    },

    createCase(draft: CaseDraft): Promise<Case> {
      return requestJson<Case>("/cases", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
    },

    patchCase(id: string, patch: Partial<CaseDraft>): Promise<Case> {
      return requestJson<Case>(`/cases/${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
    },

    async deleteCase(id: string): Promise<void> {
      // 2xx expected; no body needed. Consume the body to release the
      // socket even if the server chose to send one.
      const response = await request(`/cases/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      // Ignore any returned envelope on success.
      try {
        await response.text();
      } catch {
        // Body may have been consumed already; that's fine.
      }
    },

    async fetchDeckBlob(id: string): Promise<Blob> {
      const response = await request(
        `/cases/${encodeURIComponent(id)}/deck`,
      );
      return response.blob();
    },

    resetBaseline(): Promise<{ removedFiles: number }> {
      return requestJson<{ removedFiles: number }>("/reset-baseline", {
        method: "POST",
      });
    },

    clearAll(): Promise<{ removedCases: number }> {
      return requestJson<{ removedCases: number }>("/cases", {
        method: "DELETE",
      });
    },

    publish(opts?: { includeDecks?: boolean }): Promise<PublishResult> {
      const includeDecks = opts?.includeDecks ?? true;
      return requestJson<PublishResult>("/publish", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ includeDecks }),
      });
    },
  };
}
